import type { StaffImportProgress } from "@lafayette/schemas";
import { useRef, useState } from "react";

import { actionRow } from "../../../catalog/components/styles";
import { Button } from "../../../ui/button";
import { CommandDialog } from "../../../ui/command-dialog";
import { SectionCard } from "../../../ui/section-card";
import type { SaveMessage } from "../../commands";
import { describeReviewFailure, type ReviewCommands } from "../commands";
import { blockerMessages, plural } from "../labels";
import { nothingToImport } from "../progress";

type ApprovalPanelProps = Readonly<{
  progress: StaffImportProgress;
  commands: ReviewCommands;
  onMessage: (message: SaveMessage | null) => void;
}>;

/**
 * Step "approve". It shows what is still open and why approval is blocked. The button uses
 * `aria-disabled`, so a staff member who presses it still hears why it did not run. Approving queues the
 * processing on the server. The import creates DRAFT items. It never publishes.
 */
export function ApprovalPanel({ progress, commands, onMessage }: ApprovalPanelProps) {
  const { counts, approval, job } = progress;
  const blocked = approval.blockers.length > 0;
  const [confirming, setConfirming] = useState<"approve" | "close" | null>(null);
  const [attempted, setAttempted] = useState(false);
  const blockersRef = useRef<HTMLFieldSetElement>(null);
  const toReview =
    counts.pending + counts.ready + counts.warning + counts.duplicate + counts.invalid;

  function tryApprove() {
    if (blocked || commands.pending) {
      setAttempted(true);
      blockersRef.current?.focus();
      return;
    }
    setConfirming("approve");
  }

  return (
    <SectionCard id="import-approval" title="Approve the import">
      <p role="status" className="m-0 font-semibold">
        {plural(counts.approved, "row", "rows")} approved, {plural(counts.skipped, "row", "rows")}{" "}
        skipped, {plural(toReview, "row", "rows")} still to review.{" "}
        {plural(progress.openDecisions, "decision", "decisions")} open.
      </p>
      <p>
        Approving starts the import on the server. New items are saved as drafts. The import never
        publishes an item.
      </p>
      <div className={actionRow}>
        <Button
          type="button"
          aria-disabled={blocked || commands.pending ? "true" : undefined}
          aria-describedby={blocked ? "import-approve-blockers" : undefined}
          onClick={tryApprove}
        >
          Approve import
        </Button>
        {nothingToImport(progress) ? (
          <Button
            type="button"
            variant="secondary"
            disabled={commands.pending}
            onClick={() => setConfirming("close")}
          >
            Nothing to import: close
          </Button>
        ) : null}
      </div>
      {blocked ? (
        <fieldset
          ref={blockersRef}
          id="import-approve-blockers"
          className="m-0 min-w-0 rounded-md border border-warning-border bg-warning-soft px-4 py-3.5 [&>legend+*]:clear-left"
          aria-live={attempted ? "assertive" : "polite"}
          tabIndex={-1}
        >
          <legend className="float-left mb-1.5 w-full p-0 text-sm font-bold text-warning">
            Why approval is blocked
          </legend>
          <ul className="m-0 grid list-disc gap-1 pl-5 text-sm text-brass-strong">
            {approval.blockers.map((blocker) => (
              <li key={blocker.code}>{blockerMessages[blocker.code](blocker.count)}</li>
            ))}
          </ul>
        </fieldset>
      ) : null}
      {confirming === null ? null : (
        <ConfirmDialog
          kind={confirming}
          version={job.version}
          commands={commands}
          onMessage={onMessage}
          onClose={() => setConfirming(null)}
        />
      )}
    </SectionCard>
  );
}

function ConfirmDialog({
  kind,
  version,
  commands,
  onMessage,
  onClose,
}: Readonly<{
  kind: "approve" | "close";
  version: number;
  commands: ReviewCommands;
  onMessage: (message: SaveMessage | null) => void;
  onClose: () => void;
}>) {
  return (
    <CommandDialog
      title={kind === "approve" ? "Approve this import?" : "Close this import?"}
      description={
        kind === "approve"
          ? "The server creates or updates catalog items for the approved rows. New items are saved as drafts. Nothing is published. You can watch the progress on the next screen."
          : "Every row is skipped, so there is nothing to import. The import is closed and nothing reaches the catalog."
      }
      confirmLabel={kind === "approve" ? "Approve import" : "Close import"}
      order="confirm-first"
      mutate={async () => {
        // The version of the last read: a conflict that was refreshed since the dialog opened is used.
        const expectedVersion = commands.jobVersion() ?? version;
        const outcome =
          kind === "approve"
            ? await commands.approve({ expectedVersion })
            : await commands.close(expectedVersion);
        if (!outcome.ok) {
          const blocker = outcome.blockers[0];
          return {
            ok: false,
            failure: {
              message:
                blocker === undefined
                  ? describeReviewFailure(outcome)
                  : blockerMessages[blocker.code](blocker.count),
              retryable: false,
              keepForm: true,
            },
          };
        }
        return { ok: true, value: undefined };
      }}
      onDone={() => {
        onMessage({
          tone: "success",
          text:
            kind === "approve"
              ? "The import was approved. The server is processing it now."
              : "The import was closed. Nothing reached the catalog.",
        });
        onClose();
      }}
      onClose={onClose}
    />
  );
}
